import { useGetFeatureAccessQuery } from '@/store/features/billing.feature';
import { useOrganization } from '@clerk/react';
import {
  Feature,
  FEATURE_REQUIRED_TIER,
  V4_FEATURE_REQUIRED_TIER,
  TIER_LABELS,
  type FeatureKey,
} from '@/constants/features';

/**
 * How long a read of the entitlements stays fresh for a component that
 * mounts: within it the mount reads the cache, after it the mount asks the
 * server again. About 40 components read the entitlements, and panels, cards
 * and gates mount all the time, so a refetch on every mount was a request per
 * mount, each answered with a new `as_of`.
 */
export const ENTITLEMENTS_FRESH_SECONDS = 300;

/**
 * Reads the backend-driven entitlements matrix (GET /billing/feature-access).
 *
 * The FE is a PURE READER now — no tier math, no AppSumo carve-out sets, no
 * fallback chain. `hasFeature` is a single lookup into the dense capability map
 * the backend computes (and that the server-side gates enforce from the same
 * resolver, so advertised == enforced).
 */
export function useFeatureAccess() {
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization();
  const organizationId = organization?.id ?? '';
  const {
    currentData: data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetFeatureAccessQuery(
    { organizationId },
    {
      skip: !isOrganizationLoaded || !organizationId,
      // Trial/tier status is time-sensitive: refetch on a mount once the last
      // read is over five minutes old, on window focus, and every 5 minutes so it
      // never stays stale across the session.
      refetchOnMountOrArgChange: ENTITLEMENTS_FRESH_SECONDS,
      refetchOnFocus: true,
      pollingInterval: 5 * 60 * 1000,
    }
  );

  const hasFeature = (feature: FeatureKey): boolean => {
    // Preserve the existing catalog/demo browse surface for older plans.
    // New GTM builders and execution use the authoritative actions below.
    if (feature === Feature.AGENTS && data?.catalog_family !== 'v4')
      return true;
    return data?.features?.[feature] === true;
  };

  const tierCategory = data?.tier_category ?? 'free';
  const tierLabel = data?.tier_label ?? 'Free';
  const leadsPerListLimit = data?.limits?.leads_per_list ?? null;
  const isAppSumo = data?.is_appsumo ?? false;
  // AppSumo WITHOUT a stacked paid Stripe plan. Backend-authoritative — a
  // stacked Sumoling has is_appsumo_only=false (their paid plan supersedes the
  // AppSumo carve-outs).
  const isAppSumoOnly = data?.is_appsumo_only ?? false;
  const isStacked = data?.is_stacked ?? false;
  // AppSumo code tier (1-5, null otherwise). Unlike tier_category, safe to gate on.
  const appsumoTier = data?.appsumo_tier ?? null;
  // "Is a paying customer" — an AppSumo redemption is NOT a Stripe plan.
  const hasActivePaidStripe = data?.has_active_paid_stripe ?? false;
  const quotas = data?.quotas ?? {};

  // Upgrade-prompt copy ONLY (lock tooltips) — NOT gating. Gating is the matrix.
  const getRequiredTier = (feature: FeatureKey): string => {
    const tier = (
      data?.catalog_family === 'v4'
        ? V4_FEATURE_REQUIRED_TIER
        : FEATURE_REQUIRED_TIER
    )[feature];
    return TIER_LABELS[tier] ?? 'Starter';
  };

  // column_type -> required feature, straight from the backend taxonomy.
  const getSmartColumnFeature = (columnType: string): FeatureKey | undefined =>
    data?.smart_column_features?.[columnType] as FeatureKey | undefined;

  // Free Trial v1 state (org-level 14-day Scale trial). Non-trial orgs are
  // null. Distinct from the legacy per-user onboarding column trial below.
  const trial = data?.trial ?? null;

  // Legacy per-user onboarding column trial. The payload can report it
  // alongside an active ORG trial (both systems run server-side), but two
  // trials talking over each other is incoherent UX — the Scale trial already
  // grants every column, so the org trial wins and the legacy one goes silent
  // until the org trial is over.
  const orgTrialActive = trial?.active === true;
  const onboardingTrialActive =
    !orgTrialActive && (data?.onboarding_trial_active ?? false);
  const onboardingTrialColumns = orgTrialActive
    ? []
    : (data?.onboarding_trial_columns ?? []);

  /** True when the column is available: trial includes it, or full access. */
  const isColumnAvailableInTrial = (columnType: string): boolean => {
    if (!onboardingTrialActive) return true;
    return onboardingTrialColumns.includes(columnType);
  };

  return {
    organizationId,
    isOrganizationLoaded,
    tierCategory,
    tierLabel,
    hasFeature,
    leadsPerListLimit,
    getRequiredTier,
    getSmartColumnFeature,
    quotas,
    isLoading,
    isFetching,
    // A signed-in user can briefly have no organization while onboarding
    // creates it. That is a resolved state, not an entitlement network error.
    isReady:
      isOrganizationLoaded && (!organizationId || (!!data && !isLoading)),
    error,
    refetch,
    isAppSumo,
    isAppSumoOnly,
    isStacked,
    appsumoTier,
    hasActivePaidStripe,
    features: data?.features ?? {},
    catalogFamily: data?.catalog_family,
    actions: data?.actions,
    mcpAccess: data?.mcp_access,
    limits: data?.limits,
    Feature,
    onboardingTrialActive,
    onboardingTrialColumns,
    isColumnAvailableInTrial,
    trial,
    // Undefined until the entitlements load (or from an API that predates the
    // field). The onboarding-call offer shows only on an explicit `false`.
    reducedCreditCountry: data?.reduced_credit_country,
  };
}
